howspace.workspace = howspace.workspace || {};
howspace.workspace.contextFileModal = (() => {
    'use strict';

    const filenameWithoutExtension = filename => {
        const basename = (filename || '').split(/[\\/]/).pop();
        return basename.replace(/\.[^.]+$/, '');
    };

    const validateRequiredFields = modal => {
        if (howspace.manager?.validateForm) {
            return howspace.manager.validateForm(modal);
        }

        let valid = true;
        modal.find('input[required], textarea[required], select[required]').each(function() {
            const input = $(this);
            if (input.val().trim()) {
                howspace.form.clearInvalidField(input);
                return;
            }

            howspace.form.markInvalidField(input);
            valid = false;
        });
        return valid;
    };

    const discardUploadedFile = uploadedFileId => {
        if (!uploadedFileId) {
            return;
        }

        real.ajax.json(real.baseUri + '/manager/context/discard-uploaded-file', {
            type: 'POST',
            data: JSON.stringify({
                uploaded_file: uploadedFileId,
            }),
            logError: false,
            showErrorNotification: false,
        });
    };

    const apiErrorMessage = response => {
        let message = '';
        real.ajax.loopErrors(response, error => {
            if (message === '' && error?.message) {
                message = error.message;
            }
        });
        return message;
    };

    const showApiError = (response, fallbackMessage) => {
        const message = apiErrorMessage(response) || fallbackMessage;
        real.notification.show(message, 'error');
    };

    const createPendingUploadTracker = (modal, uploadedFileInput, submitBtn, requireUpload) => {
        const pendingUploadedFileIds = new Set();
        let committed = false;

        const track = uploadedFile => {
            if (uploadedFile?.id) {
                pendingUploadedFileIds.add(uploadedFile.id);
            }
        };

        const discard = uploadedFileId => {
            if (!uploadedFileId) {
                return;
            }

            pendingUploadedFileIds.delete(uploadedFileId);
            discardUploadedFile(uploadedFileId);
        };

        const discardAll = () => {
            pendingUploadedFileIds.forEach(discardUploadedFile);
            pendingUploadedFileIds.clear();
        };

        modal[0].addEventListener('click', e => {
            const target = e.target instanceof Element ? e.target : null;
            const deleteBtn = target?.closest('.howspace-file-delete-btn') || null;
            if (deleteBtn === null || !modal[0].contains(deleteBtn)) {
                return;
            }

            const filePreview = deleteBtn.closest('.howspace-file-uploader-preview');
            const uploadedFileId = filePreview?.dataset.uploadedFileId || uploadedFileInput.val();
            discard(uploadedFileId);
            uploadedFileInput.val('');
            if (requireUpload) {
                submitBtn.prop('disabled', true);
            }
        }, true);

        modal[0].addEventListener(howspace.components.modal.MODAL_CLOSED, () => {
            if (!committed) {
                discardAll();
            }
        });

        return {
            track,
            discardAll,
            markCommitted: () => {
                committed = true;
                pendingUploadedFileIds.clear();
            },
        };
    };

    const payloadWithChatbotUpload = (modal, chatbotUpload) => {
        const payload = real.form.getDataPayload(modal);
        if (chatbotUpload && Object.keys(chatbotUpload).length > 0) {
            payload.chatbot_context_upload = chatbotUpload;
        }
        return payload;
    };

    const initAddFileModal = (modal, options = {}) => {
        modal = $(modal);

        const uploadedFileInput = modal.find('input[name="uploaded_file"]');
        const titleInput = modal.find('input[name="title"]');
        const createBtn = modal.find('.workspace-context-create-btn');
        const pendingUploads = createPendingUploadTracker(modal, uploadedFileInput, createBtn, true);
        createBtn.prop('disabled', true);

        modal[0].addEventListener(howspace.components.modal.MODAL_CLOSED, () => {
            options.onClosed?.();
        });

        initUsageChoice(modal);

        modal.on(howspace.components.fileUploader.UPLOAD_COMPLETED, e => {
            const uploadedFile = e.detail.uploadedFiles[0] || null;
            if (uploadedFile === null) {
                return;
            }

            uploadedFileInput.val(uploadedFile.id);
            pendingUploads.track(uploadedFile);
            if (titleInput.val().trim() === '') {
                titleInput.val(filenameWithoutExtension(uploadedFile.filename));
            }
            createBtn.prop('disabled', false);
        });

        createBtn.on('click', () => {
            if (createBtn.hasClass('disabled')) {
                return;
            }

            if (uploadedFileInput.val() === '') {
                real.notification.show(howspace.translate('workspace_context_file_required'), 'error');
                return;
            }

            if (!validateRequiredFields(modal)) {
                return;
            }

            real.button.loading(createBtn, modal);
            const requestContext = options.beforeCreate?.() ?? null;

            real.ajax.json(real.baseUri + '/manager/context/create', {
                type: 'POST',
                data: JSON.stringify(payloadWithChatbotUpload(modal, options.chatbotUpload || null)),
                success: response => {
                    pendingUploads.markCommitted();
                    howspace.components.modal.close(modal);
                    options.onCreated?.(response, requestContext);
                },
                error: response => {
                    real.button.reset(createBtn, modal);
                    if (options.onCreateFailed?.(response) === false) {
                        return;
                    }
                    showApiError(response, howspace.translate('workspace_context_create_failed'));
                }
            });
        });

        if (options.initialFile instanceof File) {
            const fileUploader = modal[0].querySelector('.howspace-file-uploader');
            if (fileUploader !== null) {
                howspace.components.fileUploader.startFileUploads(fileUploader, [options.initialFile]);
            }
        }
    };

    /**
     * How the AI should use the file. The ready made instructions are the field rather than hints beside it:
     * picking one writes it into the description that gets saved, so the common case is answered without
     * writing anything. The free text box belongs to "Something else" and collapse.js opens it from that
     * option's aria-controls.
     */
    const initUsageChoice = (modal) => {
        const usageInput = modal.find('textarea[name="description"]');
        const options = modal.find('.workspace-context-ai-usage input[type="radio"]');
        if (usageInput.length === 0 || options.length === 0) {
            return;
        }

        // The box holds the user's own words and nothing else. A ready made instruction is our wording, so
        // moving off one and onto "Something else" must not leave the answer they just rejected sitting in
        // the box, and moving away and back must not lose what they had typed.
        let written = '';
        usageInput.on('input', () => {
            written = usageInput.val();
        });

        options.on('change', e => {
            // The option's value is the instruction itself, and "Something else" carries none.
            const instruction = $(e.currentTarget).val();
            usageInput.val(instruction === '' ? written : instruction);
            howspace.form.clearInvalidField(usageInput);
            blockCreateWhileUnanswered(modal);
        });

        usageInput.on('input', () => blockCreateWhileUnanswered(modal));
        blockCreateWhileUnanswered(modal);
    };

    /**
     * "Something else" with nothing written in it is the one answer that is not an answer, so the button says
     * so before it is pressed rather than after. Everything else is answered by the option that starts
     * picked, which is why there is no empty state to report.
     *
     * The wording and placement come from the button's own disableWithTooltip(), so only the state is set
     * here. The disabled attribute is not what carries it: a button holding that attribute can be neither
     * hovered nor focused, so the reason could never be read. aria-disabled says unavailable while leaving
     * it reachable, and the click handler is what refuses.
     */
    const blockCreateWhileUnanswered = (modal) => {
        const createBtn = modal.find('.workspace-context-create-btn');
        const unanswered = modal.find('.workspace-context-ai-usage input[type="radio"]:checked').val() === ''
            && modal.find('textarea[name="description"]').val().trim() === '';

        createBtn.toggleClass('disabled tooltip', unanswered);
        createBtn.attr('aria-disabled', unanswered ? 'true' : null);
    };

    const openAddFileModal = (options = {}) => {
        howspace.components.modal.show(
            real.baseUri + '/manager/context/add-file-modal',
            modal => initAddFileModal(modal, options),
        );
    };

    return {
        createPendingUploadTracker,
        initAddFileModal,
        openAddFileModal,
        showApiError,
    };
})();
